import { defaultHighlightStyle, LanguageDescription, syntaxHighlighting } from '@codemirror/language';
import { languages } from '@codemirror/language-data';
import { EditorState } from '@codemirror/state';
import { Decoration, EditorView, lineNumbers } from '@codemirror/view';
import cx from 'classnames';
import { useEffect, useMemo, useRef } from 'react';
import type { Extension, Range, Text } from '@codemirror/state';
import type { VoidFunctionComponent } from 'react';

import styles from './CodeViewer.module.less';

export type CodeViewerHighlight = {
  /**
   * Start of the highlight, as an absolute character offset into the file contents.
   */
  from: number;
  /**
   * End of the highlight, as an absolute character offset into the file contents.
   */
  to: number;
  /**
   * Class applied to the highlighted text itself.
   */
  className?: string;
  /**
   * Class applied to the background of every line the highlight spans.
   */
  lineClassName?: string;
  /**
   * Inline CSS applied to the highlighted text (eg. a per-match background colour).
   */
  style?: string;
  /**
   * Tooltip shown when hovering the highlighted text.
   */
  title?: string;
};

type HighlightedLine = {
  /**
   * Zero-based line number to highlight.
   */
  line: number;
  /**
   * Class applied to the line background.
   */
  className: string;
};

type Props = {
  fileContents: string;
  /**
   * File name (or path) used to detect the language for syntax highlighting.
   */
  fileName?: string;
  /**
   * Ranges of text to highlight, as absolute character offsets.
   */
  highlights?: CodeViewerHighlight[];
  /**
   * Line numbers to give a background class, for callers that only know
   * line numbers rather than character offsets.
   */
  highlightedLines?: HighlightedLine[];
  /**
   * Scroll the first highlight into view once the editor mounts.
   */
  scrollToFirstHighlight?: boolean;
  className?: string;
};

/**
 * Returns an identity-stable copy of the given array. Callers pass the
 * highlight arrays inline, so their identity changes every render; keying
 * on the serialized contents keeps the editor from being recreated unless
 * the values actually change.
 */
function useStableArray<T>(value: T[] | undefined): T[] {
  const key = JSON.stringify(value ?? []);
  return useMemo<T[]>(() => JSON.parse(key), [key]);
}

function clampOffset(offset: number, doc: Text): number {
  return Math.max(0, Math.min(offset, doc.length));
}

/**
 * Builds the CodeMirror decoration set for the given highlights:
 * a line-background decoration for every line a highlight spans,
 * and a mark decoration for the highlighted text itself.
 * Entries that fall outside the document are discarded.
 */
function buildDecorations(
  state: EditorState,
  highlights: CodeViewerHighlight[],
  highlightedLines: HighlightedLine[],
): Extension {
  const ranges: Array<Range<Decoration>> = [];

  highlights.forEach(({ from, to, className, lineClassName, style, title }) => {
    const clampedFrom = clampOffset(from, state.doc);
    const clampedTo = clampOffset(to, state.doc);
    if (clampedFrom >= clampedTo) {
      return;
    }
    if (lineClassName) {
      const firstLine = state.doc.lineAt(clampedFrom).number;
      /*
        The range end is exclusive, so a highlight ending exactly at a line
        boundary must not decorate the following line; resolve the last line
        from the final character actually inside the range.
      */
      const lastLine = state.doc.lineAt(clampedTo - 1).number;
      for (let lineNumber = firstLine; lineNumber <= lastLine; lineNumber++) {
        ranges.push(Decoration.line({ class: lineClassName }).range(state.doc.line(lineNumber).from));
      }
    }
    ranges.push(Decoration.mark({
      class: className,
      attributes: style || title ? {
        ...(style ? { style } : {}),
        ...(title ? { title } : {}),
      } : undefined,
    }).range(clampedFrom, clampedTo));
  });

  highlightedLines.forEach(({ line, className }) => {
    //  The callers use zero-based lines; CodeMirror lines are one-based.
    const lineNumber = line + 1;
    if (lineNumber < 1 || lineNumber > state.doc.lines) {
      return;
    }
    ranges.push(Decoration.line({ class: className }).range(state.doc.line(lineNumber).from));
  });

  return EditorView.decorations.of(Decoration.set(ranges, true));
}

/**
 * Finds the offset of the first highlight that buildDecorations actually
 * renders, skipping the entries it discards as outside the document.
 * Returns null when nothing valid is highlighted.
 */
function findScrollTarget(
  doc: Text,
  highlights: CodeViewerHighlight[],
  highlightedLines: HighlightedLine[],
): number | null {
  const firstHighlight = highlights.find(({ from, to }) => clampOffset(from, doc) < clampOffset(to, doc));
  if (firstHighlight) {
    return clampOffset(firstHighlight.from, doc);
  }
  const firstHighlightedLine = highlightedLines.find(({ line }) => line >= 0 && line < doc.lines);
  if (firstHighlightedLine) {
    return doc.line(firstHighlightedLine.line + 1).from;
  }
  return null;
}

/**
 * Loads the language support for the given file name, or null when the
 * language is unknown or fails to load.
 *
 * Language parsers are lazy-loaded as separate chunks, and the app's build
 * does not support chunk loading yet (no chunkFilename/publicPath set up for
 * prod), so in the browser this load currently fails and we fall back to
 * plain text. Syntax colouring starts working as soon as chunk loading lands.
 */
async function loadLanguage(fileName: string | undefined): Promise<Extension | null> {
  const languageDescription = fileName ? LanguageDescription.matchFilename(languages, fileName) : null;
  if (!languageDescription) {
    return null;
  }
  try {
    return await languageDescription.load();
  } catch {
    //  Language failed to load; fall back to plain text.
    return null;
  }
}

/**
 * A read-only CodeMirror 6 file viewer with line numbers,
 * language detection by file name, and match highlighting.
 */
export const CodeViewer: VoidFunctionComponent<Props> = ({
  fileContents,
  fileName,
  highlights,
  highlightedLines,
  scrollToFirstHighlight = false,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stableHighlights = useStableArray(highlights);
  const stableHighlightedLines = useStableArray(highlightedLines);

  useEffect(() => {
    const parent = containerRef.current;
    if (!parent) {
      return undefined;
    }

    let view: EditorView | null = null;
    let isCancelled = false;

    const createView = (language: Extension | null) => {
      //  A state without extensions, only used for the line/offset math when building decorations.
      const measureState = EditorState.create({ doc: fileContents });
      const editorState = EditorState.create({
        doc: fileContents,
        extensions: [
          lineNumbers(),
          syntaxHighlighting(defaultHighlightStyle),
          EditorState.readOnly.of(true),
          EditorView.editable.of(false),
          buildDecorations(measureState, stableHighlights, stableHighlightedLines),
          ...(language ? [language] : []),
        ],
      });
      view = new EditorView({ state: editorState, parent });

      if (scrollToFirstHighlight) {
        const target = findScrollTarget(editorState.doc, stableHighlights, stableHighlightedLines);
        if (target !== null) {
          view.dispatch({ effects: EditorView.scrollIntoView(target, { y: 'center' }) });
        }
      }
    };

    const createViewWithLanguage = async () => {
      const language = await loadLanguage(fileName);
      if (!isCancelled) {
        createView(language);
      }
    };
    createViewWithLanguage();

    return () => {
      isCancelled = true;
      view?.destroy();
    };
  }, [fileContents, fileName, stableHighlights, stableHighlightedLines, scrollToFirstHighlight]);

  return <div ref={containerRef} className={cx(styles.codeViewer, className)} />;
};
